"use client"

import { Calendar as CalendarIcon, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { SimplePhotoEditor as PhotoEditor, SimplePhotoEditorRef } from "./simple-photo-editor-production"
import type { PhotoItem } from "./simple-photo-editor-production"
import { useRef, forwardRef, useImperativeHandle, useEffect, useCallback, useMemo } from "react"
import { createClient } from "@/utils/supabase/client"
import { format, setHours, setMinutes } from "date-fns"
import { DEFAULT_CURRENCY } from "@/lib/currencies"
import { cn } from "@/lib/utils"
import { unreachableCategory } from "@kraahl/contract"
import { ComicCategoryStep } from "@/components/publications/creation-flow/listing-creation/steps/comic-category-step"
import { ComicDetailsStep } from "@/components/publications/creation-flow/listing-creation/steps/comic-details-step"
import type { ListingData } from "@/types/listing"
import { CategoryStep } from "@/components/publications/creation-flow/listing-creation/steps/category-step"
import { DetailsStep } from "@/components/publications/creation-flow/listing-creation/steps/details-step"
import { CardCategoryStep } from "@/components/publications/creation-flow/listing-creation/steps/card-category-step"
import { CardDetailsStep } from "@/components/publications/creation-flow/listing-creation/steps/card-details-step"

interface DetailsTabProps {
  listing: any
  formData: any
  setFormDataAction: (data: any) => void
  onFormChangeAction: (field: string, value: any) => void
  onImagesChangeAction: (images: PhotoItem[]) => void
  hideQuantity?: boolean
  isEditing: boolean
  onSave?: () => void
}

export interface DetailsTabRef {
  capturePhotoOrder: () => void
  getCurrentPhotosInOrder: () => PhotoItem[]
}

const DetailsTab = forwardRef<DetailsTabRef, DetailsTabProps>(({ 
  listing, 
  formData, 
  setFormDataAction, 
  onFormChangeAction,
  onImagesChangeAction,
  hideQuantity = false,
  isEditing,
  onSave,
}, ref) => {
  const photoEditorRef = useRef<SimplePhotoEditorRef>(null)
  const supabase = createClient()

  const listingDataForSteps: ListingData = useMemo(
    () => ({
      title: formData.title ?? "",
      description: formData.description ?? "",
      price: formData.price ?? 0,
      photos: formData.images ?? [],
      condition: formData.condition ?? "",
      grade: String(formData.grade ?? ""),
      shipping: { methods: [], customOptions: [] },
      quantity: formData.quantity ?? 1,
      brand_id: formData.brand_id,
      theme_id: formData.theme_id,
      product_type_id: formData.product_type_id,
      theme_custom_text: formData.theme_custom_text,
      brand_custom_text: formData.brand_custom_text,
      product_type_custom_text: formData.product_type_custom_text,
      currency: (listing?.currency as string) || DEFAULT_CURRENCY,
      scheduled_publish_at: formData.scheduled_publish_at ?? null,
      listingCategory: formData.listingCategory,
      cardDraft: formData.cardDraft,
      comicDraft: formData.comicDraft,
      listing_format: formData.listing_format as ListingData["listing_format"],
      // Phase 3 (2026-06-29): `accepts_offers` collapsed into
      // `listing_format` — consumers derive via `isOfferEnabledFormat()`
      // from `@kraahl/contract`.
      auto_decline_below: formData.auto_decline_below ?? null,
      listing_type:
        formData.listing_type ??
        (listing?.type === "preorder" || listing?.type === "standard" ? listing.type : undefined),
      auction_start_price: formData.auction_start_price,
      auction_reserve_price: formData.auction_reserve_price,
    }),
    [formData, listing?.currency, listing?.type]
  )

  const isCardListing = listingDataForSteps.listingCategory === "trading_card"
  const isEditingListing = Boolean(listing?.id)
  const isPreorderListing = listing?.type === "preorder"
  const isAuctionListing =
    (listing?.listing_format ?? formData.listing_format) === "auction"
  const hideSaleTypeSection = isPreorderListing || isAuctionListing

  const updateDataAction = useCallback(
    (patch: Partial<ListingData>) => {
      Object.entries(patch).forEach(([key, value]) => {
        if (!isCardListing && key === "grade" && typeof value === "string") {
          onFormChangeAction(key, parseInt(value, 10) || 0)
        } else {
          onFormChangeAction(key, value)
        }
      })
    },
    [isCardListing, onFormChangeAction]
  )

  const handlePhotosChange = (photoItems: PhotoItem[]) => {
    onImagesChangeAction(photoItems)
  }

  // Nouvelle fonction pour capturer l'ordre des photos au moment du save
  const capturePhotoOrderOnSave = () => {
    if (photoEditorRef.current) {
      photoEditorRef.current.updateParentWithCurrentOrder()
      // Photo order captured on save
    }
  }

  // Nouvelle fonction pour obtenir les photos dans l'ordre actuel
  const getCurrentPhotosInOrder = () => {
    if (photoEditorRef.current) {
      return photoEditorRef.current.getCurrentPhotosInOrder()
    }
    return []
  }

  // Exposer les fonctions au parent via useImperativeHandle
  useImperativeHandle(ref, () => ({
    capturePhotoOrder: capturePhotoOrderOnSave,
    getCurrentPhotosInOrder
  }))

  return (
    <div className="grid gap-6">
      <div className="space-y-4 pt-2">
        <PhotoEditor
          key={listing?.id ?? "new"}
          ref={photoEditorRef}
          initialPhotos={formData.images}
          onPhotosChangeAction={handlePhotosChange}
          showTitle={true}
        />
      </div>

      <div className="grid gap-4">
        {/* Exhaustive category fork — a new category must pick its detail
            steps here (`default` probe = compile error on tuple widening). */}
        {(() => {
          switch (listingDataForSteps.listingCategory) {
            case "trading_card":
              return (
                <>
            <CardCategoryStep
              data={listingDataForSteps}
              updateDataAction={updateDataAction}
              onNextAction={() => {}}
              onPrevAction={() => {}}
              hideButton
              compact
            />
            <CardDetailsStep
              data={listingDataForSteps}
              updateDataAction={updateDataAction}
              onNextAction={() => {}}
              onPrevAction={() => {}}
              hideButton
              compact
              showListingTypeSection={!isEditingListing}
              showSaleTypeSection={!hideSaleTypeSection}
              allowAuctionOption={!isEditingListing}
              hideQuantity={hideQuantity}
            />
          </>
              )
            case "comic":
              return (
                <>
                  <ComicCategoryStep
                    data={listingDataForSteps}
                    updateDataAction={updateDataAction}
                    onNextAction={() => {}}
                    onPrevAction={() => {}}
                    hideButton
                    compact
                  />
                  <ComicDetailsStep
                    data={listingDataForSteps}
                    updateDataAction={updateDataAction}
                    onNextAction={() => {}}
                    onPrevAction={() => {}}
                    hideButton
                    compact
                    showListingTypeSection={!isEditingListing}
                    showSaleTypeSection={!hideSaleTypeSection}
                    allowAuctionOption={!isEditingListing}
                    hideQuantity={hideQuantity}
                  />
                </>
              )
            case "lego":
            case undefined:
              return (
                <>
            <CategoryStep
              data={listingDataForSteps}
              updateDataAction={updateDataAction}
              onNextAction={() => {}}
              onPrevAction={() => {}}
              hideButton
              compact
            />
            <DetailsStep
              data={listingDataForSteps}
              updateDataAction={updateDataAction}
              onNextAction={() => {}}
              onPrevAction={() => {}}
              hideButton
              hideQuantity={hideQuantity}
              showListingTypeSection={!isEditingListing}
              showSaleTypeSection={!hideSaleTypeSection}
              allowAuctionOption={!isEditingListing}
              compact
            />
          </>
              )
            default:
              return unreachableCategory(listingDataForSteps.listingCategory)
          }
        })()}
        {/* ADR_LISTING_SHIP_FROM_ADDRESS Phase 3 — LocationStep removed.
            Origin is shown/edited via the ShipFromAddressPicker inside the
            shipping tab. */}

        {/* Scheduling Section - Only show if listing is scheduled */}
        {formData.scheduled_publish_at && (
          <div className="bg-muted/50 p-4 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-muted-foreground" />
                <div>
                  <Label className="text-sm font-medium">Scheduled Publication</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    This listing is scheduled to go live
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => onFormChangeAction("scheduled_publish_at", null)}
                className="h-8 w-8"
                aria-label="Remove schedule"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Display current scheduled time */}
            {(() => {
              const scheduledDate = new Date(formData.scheduled_publish_at)
              const isScheduled = scheduledDate > new Date()
              
              if (!isScheduled) {
                return (
                  <div className="text-sm text-muted-foreground bg-destructive/10 border border-destructive/20 rounded-md p-2">
                    This listing was scheduled for a past date and should be published immediately.
                  </div>
                )
              }

              const formattedDate = format(scheduledDate, 'EEEE, MMMM d, yyyy')
              const formattedTime = format(scheduledDate, 'h:mm a')
              const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

              // Get current date and time from formData to handle updates correctly
              const currentScheduledDate = formData.scheduled_publish_at ? new Date(formData.scheduled_publish_at) : scheduledDate

              return (
                <div className="space-y-3">
                  <div className="text-sm">
                    <div className="font-medium mb-1">Current Schedule:</div>
                    <div className="text-muted-foreground">
                      {formattedDate} at {formattedTime}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      ({userTimezone})
                    </div>
                  </div>

                  {/* Edit Date/Time */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Update Schedule</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="date"
                        value={format(currentScheduledDate, 'yyyy-MM-dd')}
                        onChange={(e) => {
                          const newDate = e.target.value ? new Date(e.target.value) : null
                          if (newDate) {
                            // Use current formData value to preserve time when date changes
                            const currentDate = formData.scheduled_publish_at ? new Date(formData.scheduled_publish_at) : scheduledDate
                            const [hours, minutes] = format(currentDate, 'HH:mm').split(':').map(Number)
                            let updatedDate = new Date(newDate)
                            updatedDate = setHours(updatedDate, hours)
                            updatedDate = setMinutes(updatedDate, minutes)
                            const now = new Date()
                            if (updatedDate > now) {
                              onFormChangeAction("scheduled_publish_at", updatedDate.toISOString())
                            }
                          }
                        }}
                        min={format(new Date(), 'yyyy-MM-dd')}
                        className="px-3 py-2 text-sm border border-input bg-background rounded-full focus:outline-hidden focus:ring-2 focus:ring-ring focus:border-transparent"
                      />
                      <input
                        type="time"
                        value={format(currentScheduledDate, 'HH:mm')}
                        onChange={(e) => {
                          const time = e.target.value
                          if (time) {
                            // Use current formData value to preserve date when time changes
                            const currentDate = formData.scheduled_publish_at ? new Date(formData.scheduled_publish_at) : scheduledDate
                            const [hours, minutes] = time.split(':').map(Number)
                            let updatedDate = new Date(currentDate)
                            updatedDate = setHours(updatedDate, hours)
                            updatedDate = setMinutes(updatedDate, minutes)
                            const now = new Date()
                            if (updatedDate > now) {
                              onFormChangeAction("scheduled_publish_at", updatedDate.toISOString())
                            }
                          }
                        }}
                        className="px-3 py-2 text-sm border border-input bg-background rounded-full focus:outline-hidden focus:ring-2 focus:ring-ring focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              )
            })()}
          </div>
        )}
      </div>
    </div>
  )
})

DetailsTab.displayName = 'DetailsTab'

export default DetailsTab
export { DetailsTab }
export type { DetailsTabProps }
